import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { createQueryAsyncThunk } from "../../app/createApiAsyncThunk";
import { RootState } from "../../app/store";
import { Paginated, Relation } from "../../common/types/Paginated";
import { EntitySliceStatePaged } from "../../common/types/commonTypes";
import { getCustomerOrganizationsPaged } from "./customerAPI";
import {
  CustomerOrganizationsPageFilters,
  CustomerOrganizationsResult,
} from "./types/queries";

type SliceState = EntitySliceStatePaged<CustomerOrganizationsResult>;

const customerOrganizationListAdapter =
  createEntityAdapter<CustomerOrganizationsResult>();

export const fetchCustomerListPage = createQueryAsyncThunk<
  Paginated<CustomerOrganizationsResult[]>,
  CustomerOrganizationsPageFilters
>("customerOrganizationList/fetchPage", getCustomerOrganizationsPaged);

const initialState: SliceState =
  customerOrganizationListAdapter.getInitialState({
    status: "idle",
    error: null,
    cursor: null,
    hits: { value: 0, relation: Relation.DEFAULT },
  });

export const customerOrganizationListSlice = createSlice({
  name: "customerOrganizationList",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomerListPage.pending, (state) => {
        state.status = "pending";
      })
      .addCase(fetchCustomerListPage.fulfilled, (state, action) => {
        // If cursor is null/undefined, it's a new search - replace all
        // If cursor exists, it's pagination - add/update items
        if (!action.meta.arg.cursor) {
          customerOrganizationListAdapter.setAll(state, action.payload.data);
        } else {
          customerOrganizationListAdapter.upsertMany(
            state,
            action.payload.data,
          );
        }

        state.cursor = action.payload.cursor;
        state.hits = action.payload.hits;
        state.status = "succeeded";
      })
      .addCase(fetchCustomerListPage.rejected, (state, action) => {
        state.error = action.error.message ?? null;
        state.status = "failed";
      });
  },
});

export const customerOrganizationListReducer =
  customerOrganizationListSlice.reducer;

export const { selectAll: selectAllCustomerOrganizationsList } =
  customerOrganizationListAdapter.getSelectors(
    (state: RootState) => state.customerOrganizationList,
  );
